import { derived, get, writable } from 'svelte/store';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { Database, Tables, TablesInsert, TablesUpdate } from '../integrations/supabase/types';
import { xpActions } from './xp';

const MS_IN_DAY = 24 * 60 * 60 * 1000;

type UserProgressRow = Tables<'user_progress'>;
type UserProgressInsert = TablesInsert<'user_progress'>;
type LearningSessionRow = Tables<'learning_sessions'>;
type LearningSessionInsert = TablesInsert<'learning_sessions'>;
type DailyStatsRow = Tables<'daily_statistics'>;
type DailyStatsInsert = TablesInsert<'daily_statistics'>;
type DailyStatsUpdate = TablesUpdate<'daily_statistics'>;

const supabaseClient: SupabaseClient<Database> = supabase;
const fromTable = <T extends keyof Database['public']['Tables']>(table: T) =>
  supabaseClient.from(table) as any;

export interface UserProgressState {
  daily_streak: number;
  longest_streak: number;
  weekly_goal_minutes: number;
  weekly_minutes_completed: number;
  weekly_cards_goal: number;
  weekly_cards_learned: number;
  weekly_streak: number;
  longest_weekly_streak: number;
  last_week_goal_met: boolean;
  total_study_time_minutes: number;
  last_study_date: string | null;
  week_start_date: string | null;
}

const defaultProgress: UserProgressState = {
  daily_streak: 0,
  longest_streak: 0,
  weekly_goal_minutes: 300,
  weekly_minutes_completed: 0,
  weekly_cards_goal: 10,
  weekly_cards_learned: 0,
  weekly_streak: 0,
  longest_weekly_streak: 0,
  last_week_goal_met: false,
  total_study_time_minutes: 0,
  last_study_date: null,
  week_start_date: null,
};

function mapRowToState(row: UserProgressRow | null): UserProgressState {
  if (!row) return { ...defaultProgress };
  return {
    daily_streak: row.daily_streak ?? 0,
    longest_streak: row.longest_streak ?? 0,
    weekly_goal_minutes: row.weekly_goal_minutes ?? defaultProgress.weekly_goal_minutes,
    weekly_minutes_completed: row.weekly_minutes_completed ?? 0,
    weekly_cards_goal: row.weekly_cards_goal ?? defaultProgress.weekly_cards_goal,
    weekly_cards_learned: row.weekly_cards_learned ?? 0,
    weekly_streak: row.weekly_streak ?? 0,
    longest_weekly_streak: row.longest_weekly_streak ?? 0,
    last_week_goal_met: row.last_week_goal_met ?? false,
    total_study_time_minutes: row.total_study_time_minutes ?? 0,
    last_study_date: row.last_study_date ?? null,
    week_start_date: row.week_start_date ?? null,
  };
}

export const userProgress = writable<UserProgressState>({ ...defaultProgress });
export const recentSessions = writable<LearningSessionRow[]>([]);
export const todayStudyMinutes = writable(0);
export const isLoadingProgress = writable(false);
export const progressError = writable<string | null>(null);

export const weeklyProgressPercentage = derived(userProgress, ($progress) => {
  if ($progress.weekly_goal_minutes <= 0) return 0;
  return Math.min(
    100,
    Math.round(
      ($progress.weekly_minutes_completed / Math.max($progress.weekly_goal_minutes, 1)) * 100
    )
  );
});

const LEAGUES = [
  { min: 0, label: 'Cínová liga' },
  { min: 3, label: 'Bronzová liga' },
  { min: 7, label: 'Stříbrná liga' },
  { min: 14, label: 'Zlatá liga' },
  { min: 30, label: 'Platinová liga' },
  { min: 50, label: 'Diamantová liga' },
];

export const leagueTitle = derived(userProgress, ($progress) => {
  // Liga se nyní počítá podle weekly_streak místo daily_streak
  const streak = $progress.weekly_streak;
  const league = LEAGUES.reduce((current, candidate) =>
    streak >= candidate.min ? candidate : current
  );
  return league.label;
});

function startOfDayISO(date = new Date()): string {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return start.toISOString().split('T')[0];
}

function shouldResetWeeklyProgress(weekStartDate: string | null, todayISO: string): boolean {
  if (!weekStartDate) return true;
  const start = new Date(weekStartDate);
  const today = new Date(todayISO);
  const diffDays = Math.floor((today.getTime() - start.getTime()) / MS_IN_DAY);
  return diffDays >= 7;
}

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabaseClient.auth.getUser();
  return user?.id ?? null;
}

export const progressActions = {
  async loadProgress() {
    isLoadingProgress.set(true);
    progressError.set(null);

    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        userProgress.set({ ...defaultProgress });
        todayStudyMinutes.set(0);
        recentSessions.set([]);
        return;
      }

      const { data, error } = await fromTable('user_progress')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      const progressRow = data as UserProgressRow | null;

      if (!progressRow) {
        await progressActions.createDefaultProgress(userId);
      } else {
        userProgress.set(mapRowToState(progressRow));
      }

      await progressActions.loadTodayMinutes(userId);
      await progressActions.loadRecentSessions();
    } catch (error) {
      console.error('Error loading progress:', error);
      progressError.set(error instanceof Error ? error.message : 'Nepodařilo se načíst pokrok.');
      userProgress.set({ ...defaultProgress });
      todayStudyMinutes.set(0);
      recentSessions.set([]);
    } finally {
      isLoadingProgress.set(false);
    }
  },

  async loadTodayMinutes(userId?: string) {
    try {
      const targetUserId = userId ?? (await getCurrentUserId());
      if (!targetUserId) {
        todayStudyMinutes.set(0);
        return;
      }

      const todayISO = startOfDayISO();
      const { data, error } = await fromTable('daily_statistics')
        .select('*')
        .eq('user_id', targetUserId)
        .eq('stat_date', todayISO)
        .maybeSingle();

      if (error) throw error;

      const statsRow = data as DailyStatsRow | null;
      todayStudyMinutes.set(statsRow?.study_time_minutes ?? 0);
    } catch (error) {
      console.error('Error loading today minutes:', error);
      todayStudyMinutes.set(0);
    }
  },

  async createDefaultProgress(userId: string) {
    const todayISO = startOfDayISO();
    const payload: UserProgressInsert = {
      user_id: userId,
      daily_streak: 0,
      longest_streak: 0,
      weekly_goal_minutes: defaultProgress.weekly_goal_minutes,
      weekly_minutes_completed: 0,
      weekly_cards_goal: defaultProgress.weekly_cards_goal,
      weekly_cards_learned: 0,
      weekly_streak: 0,
      longest_weekly_streak: 0,
      last_week_goal_met: false,
      total_study_time_minutes: 0,
      last_study_date: null,
      week_start_date: todayISO,
    };

    const { data, error } = await fromTable('user_progress')
      .insert(payload as Record<string, unknown>)
      .select('*')
      .maybeSingle();

    if (error) throw error;

    const progressRow = data as UserProgressRow | null;
    userProgress.set(mapRowToState(progressRow));
    todayStudyMinutes.set(0);
  },

  async loadRecentSessions(limit = 5) {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        recentSessions.set([]);
        return;
      }

      const { data, error } = await fromTable('learning_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('start_time', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const sessions = (data ?? []) as LearningSessionRow[];
      recentSessions.set(sessions);
    } catch (error) {
      console.error('Error loading recent sessions:', error);
      recentSessions.set([]);
    }
  },

  async startSession(
    targetLanguageId?: string,
    categoryId?: string,
    sessionType: string = 'flashcards'
  ): Promise<string | null> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) return null;

      const payload: LearningSessionInsert = {
        user_id: userId,
        target_language_id: targetLanguageId ?? null,
        category_id: categoryId ?? null,
        start_time: new Date().toISOString(),
        cards_studied: 0,
        cards_learned: 0,
        session_type: sessionType,
      };

      const { data, error } = await fromTable('learning_sessions')
        .insert(payload as Record<string, unknown>)
        .select('id')
        .maybeSingle();

      if (error) throw error;
      const inserted = data as { id: string } | null;
      return inserted?.id ?? null;
    } catch (error) {
      console.error('Error starting session:', error);
      return null;
    }
  },

  async endSession(sessionId: string, cardsStudied: number, cardsLearned: number) {
    try {
      const { data, error } = await fromTable('learning_sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (error) throw error;

      const sessionRow = data as LearningSessionRow | null;
      if (!sessionRow?.start_time) return;

      const endTime = new Date();
      const startTime = new Date(sessionRow.start_time);
      const durationMinutes = Math.max(
        1,
        Math.round((endTime.getTime() - startTime.getTime()) / 60000)
      );

      const { error: updateError } = await fromTable('learning_sessions')
        .update({
          end_time: endTime.toISOString(),
          duration_minutes: durationMinutes,
          cards_studied: cardsStudied,
          cards_learned: cardsLearned,
        } as Record<string, unknown>)
        .eq('id', sessionId);

      if (updateError) throw updateError;

      await progressActions.updateProgress(durationMinutes, cardsStudied, cardsLearned);
      await progressActions.loadRecentSessions();
    } catch (error) {
      console.error('Error ending session:', error);
    }
  },

  async updateProgress(minutesStudied: number, cardsStudied: number, cardsLearned: number) {
    try {
      const userId = await getCurrentUserId();
      if (!userId) return;

      const current = get(userProgress);
      const todayISO = startOfDayISO();
      const yesterdayISO = startOfDayISO(new Date(Date.now() - MS_IN_DAY));

      // Update daily streak (keep for other statistics)
      let dailyStreak = current.daily_streak;
      if (current.last_study_date !== todayISO) {
        dailyStreak = current.last_study_date === yesterdayISO ? dailyStreak + 1 : 1;
      }

      const longestStreak = Math.max(current.longest_streak, dailyStreak);
      const resetWeek = shouldResetWeeklyProgress(current.week_start_date, todayISO);

      // Calculate weekly progress
      let weeklyStreak = current.weekly_streak;
      let longestWeeklyStreak = current.longest_weekly_streak;
      let lastWeekGoalMet = current.last_week_goal_met;
      let weeklyMinutes: number;
      let weeklyCardsLearned: number;
      let weekStartDate: string;

      if (resetWeek) {
        // Check if last week's goal was met (at least one criterion)
        const lastWeekMinutesMet = current.weekly_minutes_completed >= current.weekly_goal_minutes;
        const lastWeekCardsMet = current.weekly_cards_learned >= current.weekly_cards_goal;
        lastWeekGoalMet = lastWeekMinutesMet || lastWeekCardsMet;

        // Update weekly streak
        if (lastWeekGoalMet) {
          weeklyStreak = current.weekly_streak + 1;
        } else {
          weeklyStreak = 0; // Reset streak if goal not met
        }

        longestWeeklyStreak = Math.max(longestWeeklyStreak, weeklyStreak);

        // Reset weekly counters
        weeklyMinutes = minutesStudied;
        weeklyCardsLearned = cardsLearned;
        weekStartDate = todayISO;
      } else {
        // Continue current week
        weeklyMinutes = current.weekly_minutes_completed + minutesStudied;
        weeklyCardsLearned = current.weekly_cards_learned + cardsLearned;
        weekStartDate = current.week_start_date ?? todayISO;
      }

      const totalMinutes = current.total_study_time_minutes + minutesStudied;

      const updatePayload = {
        daily_streak: dailyStreak,
        longest_streak: longestStreak,
        weekly_minutes_completed: weeklyMinutes,
        weekly_cards_learned: weeklyCardsLearned,
        weekly_streak: weeklyStreak,
        longest_weekly_streak: longestWeeklyStreak,
        last_week_goal_met: lastWeekGoalMet,
        total_study_time_minutes: totalMinutes,
        last_study_date: todayISO,
        week_start_date: weekStartDate,
        updated_at: new Date().toISOString(),
      } as Record<string, unknown>;

      const { error } = await fromTable('user_progress')
        .update(updatePayload)
        .eq('user_id', userId);

      if (error) throw error;

      await upsertDailyStats(userId, todayISO, minutesStudied, cardsStudied, cardsLearned);

      todayStudyMinutes.update((value) =>
        current.last_study_date === todayISO ? value + minutesStudied : minutesStudied
      );

      userProgress.set({
        daily_streak: dailyStreak,
        longest_streak: longestStreak,
        weekly_goal_minutes: current.weekly_goal_minutes,
        weekly_minutes_completed: weeklyMinutes,
        weekly_cards_goal: current.weekly_cards_goal,
        weekly_cards_learned: weeklyCardsLearned,
        weekly_streak: weeklyStreak,
        longest_weekly_streak: longestWeeklyStreak,
        last_week_goal_met: lastWeekGoalMet,
        total_study_time_minutes: totalMinutes,
        last_study_date: todayISO,
        week_start_date: weekStartDate,
      });

      // Award XP asynchronously (don't await to avoid blocking)
      this.awardXPForProgress(
        cardsLearned,
        resetWeek,
        lastWeekGoalMet,
        current,
        dailyStreak,
        weeklyStreak,
        todayISO
      ).catch((err) => console.error('Error awarding XP:', err));
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  },

  // Award XP based on progress achievements
  async awardXPForProgress(
    cardsLearned: number,
    resetWeek: boolean,
    lastWeekGoalMet: boolean,
    previousState: UserProgressState,
    newDailyStreak: number,
    newWeeklyStreak: number,
    todayISO: string
  ) {
    try {
      // 1. Award XP for learned cards
      if (cardsLearned > 0) {
        for (let i = 0; i < cardsLearned; i++) {
          await xpActions.awardXP('card_learned');
        }
      }

      // 2. Check for daily card milestone (10 cards in a day)
      const { data: todayStats } = await fromTable('daily_statistics')
        .select('cards_learned')
        .eq('user_id', await getCurrentUserId())
        .eq('stat_date', todayISO)
        .maybeSingle();

      const todayCardsLearned = (todayStats as any)?.cards_learned ?? 0;
      if (todayCardsLearned >= 10 && (todayCardsLearned - cardsLearned) < 10) {
        await xpActions.awardXP('daily_10_cards');
      }

      // 3. Check for weekly goal completion (when week resets)
      if (resetWeek) {
        const minutesGoalMet = previousState.weekly_minutes_completed >= previousState.weekly_goal_minutes;
        const cardsGoalMet = previousState.weekly_cards_learned >= previousState.weekly_cards_goal;

        if (lastWeekGoalMet) {
          // Award XP for each goal met
          if (minutesGoalMet) {
            await xpActions.awardXP('weekly_goal_minutes');
          }
          if (cardsGoalMet) {
            await xpActions.awardXP('weekly_goal_cards');
          }
          // Bonus if both goals met
          if (minutesGoalMet && cardsGoalMet) {
            await xpActions.awardXP('weekly_goal_both');
          }
        } else {
          // Penalty for not meeting weekly goal
          await xpActions.awardXP('weekly_goal_failed');
        }
      }

      // 4. Check for daily streak milestones
      if (newDailyStreak === 7 && previousState.daily_streak < 7) {
        await xpActions.awardXP('daily_streak_7');
      }

      // 5. Check for weekly streak milestones
      if (newWeeklyStreak === 7 && previousState.weekly_streak < 7) {
        await xpActions.awardXP('weekly_streak_7');
      }
      if (newWeeklyStreak === 30 && previousState.weekly_streak < 30) {
        await xpActions.awardXP('weekly_streak_30');
      }
    } catch (error) {
      console.error('Error in awardXPForProgress:', error);
    }
  },
};

async function upsertDailyStats(
  userId: string,
  statDate: string,
  minutesStudied: number,
  cardsStudied: number,
  cardsLearned: number
) {
  const { data, error } = await fromTable('daily_statistics')
    .select('*')
    .eq('user_id', userId)
    .eq('stat_date', statDate)
    .maybeSingle();

  if (error) throw error;

  const existing = data as DailyStatsRow | null;

  if (!existing) {
    const payload: DailyStatsInsert = {
      user_id: userId,
      stat_date: statDate,
      study_time_minutes: minutesStudied,
      cards_studied: cardsStudied,
      cards_learned: cardsLearned,
      tests_taken: 0,
    };

    const { error: insertError } = await fromTable('daily_statistics').insert(
      payload as Record<string, unknown>
    );
    if (insertError) throw insertError;
    return;
  }

  const payload: DailyStatsUpdate = {
    study_time_minutes: (existing.study_time_minutes ?? 0) + minutesStudied,
    cards_studied: (existing.cards_studied ?? 0) + cardsStudied,
    cards_learned: (existing.cards_learned ?? 0) + cardsLearned,
  };

  const { error: updateError } = await fromTable('daily_statistics')
    .update(payload as Record<string, unknown>)
    .eq('id', existing.id);

  if (updateError) throw updateError;
}
